import { ROUTES, RouteName, type SignUpSource } from '@/constants';
import { useGeo, useGlobalState } from '@/hooks';
import { useAuth } from '@/hooks/useAuth.hook';
import { type IUserInfo, LoginMethod } from '@/interfaces';
import type { LoginReplyFlags } from '@/interfaces/tracking/trackingOnboardingPopup.interfaces';
import { useLoginCta } from '@/modules/pmt/hooks/useLoginCta.hook';
import { getGaClientId, getSessionId } from '@/modules/pmt/utils/ga4.utils';
import { userInfoModify } from '@/utils';
import { useState } from 'react';

type HandleSuccess = (code: string) => void;

type UseLinkedInState = (args: {
  shouldReloadAfterSuccess?: boolean;
  customHandleSuccess?: (userInfo: IUserInfo, flags?: LoginReplyFlags) => void;
  origin: string;
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
}) => {
  isLoading: boolean;
  handleSuccess: HandleSuccess;
  redirectUri: string;
};

const useLinkedInState: UseLinkedInState = ({
  shouldReloadAfterSuccess = true,
  customHandleSuccess,
  origin,
  signUpSource,
  newsletterOptIn,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { geoInfo } = useGeo();
  const { isMultiLanguagePage } = useGlobalState();

  const { authPM } = useAuth();

  const redirectUri = `${origin}${ROUTES[RouteName.SignInWithLinkedIn].path}`;

  const cta = useLoginCta();

  const handleSuccess: HandleSuccess = async (code) => {
    setIsLoading(true);

    try {
      const response = await authPM({
        mediumOrigin: 'linkedin',
        code,
        redirectUri,
        country: geoInfo.country,
        signUpSource,
        newsletterOptIn,
        gaClientId: getGaClientId(),
        gaSessionId: getSessionId(isMultiLanguagePage),
        eventTimestamp: Date.now.toString(),
        cta,
        authMethod: LoginMethod.LinkedIn,
      });

      const modifiedUserInfo = userInfoModify(response.user_info);

      if (shouldReloadAfterSuccess) {
        location.reload();
      }

      customHandleSuccess?.(modifiedUserInfo, { created: response.created }); // Change to optional chain: biome rule
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSuccess,
    redirectUri,
  };
};

export default useLinkedInState;
